import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../services/api';
import { normalizeHtmlMediaUrls, resolveMediaUrl } from '../utils/media';
import MediaImage from '../components/MediaImage';
import { formatChinaDate } from '../utils/date';
import { MegaphoneIcon } from '@heroicons/react/24/outline';

type NewsDetailItem = {
  _id: string;
  title: string;
  category?: 'activity' | 'conference' | 'graduation' | string;
  coverUrl?: string;
  publishedAt?: string;
  content?: string; // HTML
};

type Neighbor = {
  id: string;
  title: string;
  publishedAt?: string;
};

const renderHtml = (html?: string) => ({ __html: html || '' });
const stripHtml = (value: string = '') =>
  value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
const categoryLabelMap: Record<string, string> = {
  activity: 'Activities',
  conference: 'Conferences',
  graduation: 'Graduation',
};

const NewsDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [item, setItem] = useState<NewsDetailItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [prev, setPrev] = useState<Neighbor | null>(null);
  const [next, setNext] = useState<Neighbor | null>(null);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      try {
        setLoading(true);
        const res = await api.get<NewsDetailItem>(`/news/${id}`);
        setItem(res.data);
        setError(null);
      } catch (err) {
        console.error(err);
        setError('News item not found or unavailable.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const loadNav = async () => {
      try {
        const res = await api.get<{ prev: Neighbor | null; next: Neighbor | null }>(
          `/news/${id}/navigation`
        );
        setPrev(res.data.prev);
        setNext(res.data.next);
      } catch {
        setPrev(null);
        setNext(null);
      }
    };
    loadNav();
  }, [id]);

  const publishedDate = item?.publishedAt
    ? formatChinaDate(item.publishedAt, 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  return (
    <section className="bg-surface min-h-full">
      <div className="mx-auto max-w-screen-2xl px-8 pb-20 pt-12">
        {loading ? (
          <p className="text-on-surface-variant">Loading...</p>
        ) : error ? (
          <div className="rounded-md bg-error-container px-4 py-3 text-sm text-on-error-container">
            {error}
          </div>
        ) : !item ? (
          <p className="text-on-surface-variant">News item not found.</p>
        ) : (
          <main className="space-y-10">
              <header className="space-y-6">
                <nav className="flex items-center gap-2 text-sm text-on-surface-variant">
                  <Link to="/" className="hover:text-primary">Home</Link>
                  <span>/</span>
                  <Link to="/news" className="hover:text-primary">News</Link>
                </nav>

                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded-full bg-secondary-container px-3 py-1 text-xs font-semibold uppercase tracking-wider text-on-secondary-container">
                    {categoryLabelMap[item.category || ''] || item.category || 'News'}
                  </span>
                  {publishedDate && (
                    <span className="text-sm text-on-surface-variant">{publishedDate}</span>
                  )}
                </div>

                <h1
                  className="font-serif text-4xl leading-tight text-primary md:text-6xl"
                  dangerouslySetInnerHTML={renderHtml(item.title)}
                />

                <div className="flex items-center gap-4 border-y border-outline-variant/30 py-4">
                  <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-surface-container">
                    <MegaphoneIcon className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-on-surface">Research Group News Office</p>
                    <p className="text-xs text-on-surface-variant">Official release</p>
                  </div>
                </div>
              </header>

              {item.coverUrl && (
                <figure className="overflow-hidden rounded-lg ambient-shadow">
                  <div className="flex justify-center bg-surface-container">
                    <MediaImage
                      src={item.coverUrl}
                      alt={stripHtml(item.title)}
                      className="max-h-[min(70vh,720px)] w-full object-contain"
                    />
                  </div>
                </figure>
              )}

              <div className="prose prose-sm md:prose-base max-w-none text-on-surface prose-headings:font-serif prose-headings:text-primary prose-p:leading-relaxed prose-p:text-on-surface prose-strong:text-on-surface prose-a:text-primary hover:prose-a:underline">
                <div
                  dangerouslySetInnerHTML={{
                    __html: normalizeHtmlMediaUrls(item.content || ''),
                  }}
                />
              </div>

              {(prev || next) && (
                <nav className="grid gap-4 md:grid-cols-2">
                  <div className="um-card bg-surface-container-low p-5">
                    <div className="mb-2 text-xs uppercase tracking-wider text-on-surface-variant">Previous</div>
                    {prev ? (
                      <Link to={`/news/${prev.id}`} className="font-serif text-xl text-primary hover:underline">
                        {stripHtml(prev.title)}
                      </Link>
                    ) : (
                      <p className="text-on-surface-variant">None</p>
                    )}
                  </div>
                  <div className="um-card bg-surface-container-low p-5">
                    <div className="mb-2 text-xs uppercase tracking-wider text-on-surface-variant">Next</div>
                    {next ? (
                      <Link to={`/news/${next.id}`} className="font-serif text-xl text-primary hover:underline">
                        {stripHtml(next.title)}
                      </Link>
                    ) : (
                      <p className="text-on-surface-variant">None</p>
                    )}
                  </div>
                </nav>
              )}
          </main>
        )}
      </div>
    </section>
  );
};

export default NewsDetail;

